import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
} from 'react';
import { graphql, StaticQuery } from 'gatsby';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import DrawSVGPlugin from 'gsap/DrawSVGPlugin';
import { SplitWordsAnimation, Section, Image } from './common';
import { HorizontalLine } from './common/Lines';
import './Schrodinger.scss';

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);

const vh = coef => window.innerHeight * (coef / 100);

const SchrodingerTemplate = (props) => {
  const lineScrollTriggerRef = useRef();
  const catScrollTriggerRef = useRef();
  const lineAnimationRef = useRef();
  const catAnimationRef = useRef();
  const lineSvgRef = useRef();
  const catSvgRef = useRef();

  const { images } = props;
  const { schrodingerPhoto } = images;

  const [showTitle, setShowTitle] = useState(false);

  const revealTitle = useCallback(() => {
    setShowTitle(true);
  }, []);

  const initLineScrollTrigger = useCallback(() => {
    const tl = gsap.timeline();
    const scrollTrigger = ScrollTrigger.create({
      trigger: lineSvgRef.current,
      pin: false,
      once: true,
      start: `top-=${vh(30)}`,
      end: `bottom-=${vh(60)}`,
      scrub: 0,
      animation: tl,
    });
    tl.fromTo(lineSvgRef.current, {
      drawSVG: '0%',
    }, {
      drawSVG: '100%',
      ease: 'none',
    }, 0);
    lineAnimationRef.current = tl;
    lineScrollTriggerRef.current = scrollTrigger;
  }, []);

  const initCatScrollTrigger = useCallback(() => {
    const tl = gsap.timeline();
    const scrollTrigger = ScrollTrigger.create({
      trigger: catSvgRef.current,
      pin: false,
      once: true,
      start: `top-=${vh(60)}`,
      end: `bottom-=${vh(50)}`,
      scrub: 0,
      animation: tl,
    });
    tl.fromTo(catSvgRef.current, {
      drawSVG: '0%',
    }, {
      drawSVG: '100%',
      ease: 'none',
      onComplete: revealTitle,
    }, 0);
    catAnimationRef.current = tl;
    catScrollTriggerRef.current = scrollTrigger;
  }, []);

  useEffect(() => {
    if (lineSvgRef.current) {
      initLineScrollTrigger();
      return () => {
        lineAnimationRef.current.kill();
        lineScrollTriggerRef.current.kill();
      };
    }
  }, [lineSvgRef]);

  useEffect(() => {
    if (catSvgRef.current) {
      initCatScrollTrigger();
      return () => {
        catAnimationRef.current.kill();
        catScrollTriggerRef.current.kill();
      };
    }
  }, [catSvgRef]);

  return (
    <>
      <Section className="schrodinger-line-drawing">
        <div className="line-svg">
          <svg className="svg-line" preserveAspectRatio="none" viewBox="0 0 560 759" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path ref={lineSvgRef} d="M280 0V759" />
          </svg>
        </div>
        <div className="cat-svg">
          <svg viewBox="0 0 560 764" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path ref={catSvgRef} d="M280 1.5C279 25.0127 285.061 75.1197 295.789 96.5844C301.958 108.927 309.984 121.211 317.09 133.652C320.101 138.923 321.166 148.121 324.891 152.261C328.234 155.977 331.668 169.993 331.941 174.922C332.386 182.921 338.21 216.792 323.166 216.792C314.96 216.792 308.463 213.765 303.889 205.987C300.545 200.299 295.479 193.553 291.138 188.729C283.557 180.301 293.484 179.848 300.139 181.75C307.704 183.913 307.749 193.584 304.939 199.909C298.371 214.695 282.569 217.58 275.537 231.649C272.735 237.256 269.837 243.712 269.837 249.883C269.837 259.234 263.535 269.507 260.386 278.172C258.113 284.426 255.231 288.861 251.058 294.079C246.513 299.762 260.386 320.503 295.789 320.503C314.644 320.503 323.804 313.258 334.642 304.584C336.5 300 334.642 304.584 338.692 287.856C338.995 279.969 329.241 277.699 329.241 270.017C329.241 256.954 338.224 225.608 329.241 214.054C341.558 198.328 326.934 166.861 327.891 163.442C327.891 162.245 323.566 150.467 324.891 153.564C328.49 156.805 339.388 159.924 342.904 163.442C348.213 168.752 355.718 178.006 362.081 181.75C364.131 182.957 366.5 186.682 368.137 188.729C370.592 191.799 404.135 253.525 421.348 278.172C444 318.147 444 397.578 444 431.545C444 449.065 441.625 457.279 437.5 483.5C428.5 506 429.895 500 422.5 520C409 556.513 396.746 584.143 392.021 606C388.817 614.333 385.271 632 389.332 687.365C389.332 694.5 389.332 695.504 389.332 703.183C389.332 709.507 400.896 717.883 392.021 717.883C385.793 717.883 381.055 714.993 392.021 712.556C395.491 711.784 400.5 712.556 410.247 715.857C421.348 721.364 417 717.883 421.348 727.638C421.348 735.228 415.296 740.844 408.222 740.844C401.416 740.844 392.906 742.467 385.271 740.769C368.137 740.844 362.081 731.465 355.352 709.779C351.789 703.006 350.275 686.151 348.623 678.714C345.935 666.609 338.692 570.662 342.904 512.011C342.904 485.24 332.5 400 325.409 393.802C324 392 310.077 389.987 303.664 388.154C294.258 385.465 284.068 390.754 275.537 393.802C238.273 415.416 218.76 431.235 201.657 450.419C188.154 465.565 164.594 503.597 160.178 517.987C154.266 537.254 152.934 550.939 149.603 565.935C146.311 580.751 149.101 597.441 152.303 611.857C153.457 617.053 152.303 632.173 156.856 651.015C160.599 666.503 168.094 682.09 167.758 681.985C175.159 695.04 186.491 706.776 193.556 717.883C207.793 733.817 208.099 731.872 212.503 735.837C217.338 740.189 223.985 740.1 230.684 740.844C238.465 741.709 246.678 744.475 254.311 746.172C264.931 748.533 278.427 746.247 289.413 746.247C297.887 746.247 307 745.747 319.5 741.5C325.5 738 327.595 733.434 319.69 725.061C313.987 719.019 311.77 723.384 303.341 723.384C296.5 721.364 299.64 721.028 295.266 719.008C292.5 718.5 291 721 293.248 723.384C295.266 724.5 296.075 724.845 300.649 721.364C303.341 717.5 303.341 709.876 304.551 703.183C307.715 685.682 309.597 699.481 307.715 661.112C305.581 617.617 270.907 592.788 261.151 584.038C245.138 572.6 227.307 569.571 212.368 570.662C190.972 572.226 169.776 582.361 153.728 597C145.889 605.584 139.834 608.95 129.068 632.173C116.956 664.484 118.302 673.908 125.376 696.273C134.451 714.969 146.5 731.5 158.078 740.544C169.381 749.373 170.5 750.5 178 753C185.5 755 192 755.095 201.5 754C210.178 753 224.983 746.172 227.984 722.61C227.984 720.575 225.791 688.955 214 669.5C209.816 663.222 201.346 656.363 196 651.015C193.642 648.656 182.5 642 182.5 642" />
          </svg>
        </div>
      </Section>
      <Section className="schrodinger-text-section" hasGrid>
        <div className="title">
          <div className="title-container">
            <SplitWordsAnimation animationDelay={0} show={showTitle}>
              <h2>Let's start with a Cat</h2>
            </SplitWordsAnimation>
          </div>
        </div>
        <div className="column column-left">
          <p>The study of quantum physics surrounds the ‘quanta’, the smallest unit that makes up a physical entity. It is  what we can observe at an atomic and sub-atomic level - electrons, photons, neutrons, or generally, particles. </p>
          <p>Matter makes up everything in our universe, and to study it at its smallest form is thought to be an important part of forming our fundamental framework for understanding and describing nature.</p>
        </div>
        <div className="column column-right">
          <p>The thing is, that these quanta behave very strangely, so as a result the study of quantum mechanics can lead to some very strange conclusions about our physical world.</p>
          <p>Perhaps most noteable of these strange behaviours is that we cannot define an electron or photons position, trajectory, or  momentum (this is the Heisenberg uncertainty principle). Quanta exist as probability distributions. Unless they're interacting with something (i.e. "being observed") they don't have a definite location.</p>
        </div>
        <HorizontalLine className="section-divider" transformOrigin="0 50%" />
        <div className="column column-left">
          <p>The year is 1935, we’re in a lecture hall in Princeton University in New Jersey. There, Erwin Schrödinger is pointing out the absurd nature of quantum mechanics by using an analogy involving a cat, a box and a vial of poison.</p>
          <div className="photo-container">
            <Image
              image={{ localFile: schrodingerPhoto }}
            />
          </div>
        </div>
        <div className="column column-right">
          <p>You put a cat in a box, and have no way to observe the cat while the box is closed. In the box you also put a device that will release poison based on a quantum observation that has a 50% chance of being "true" (like an electron moving one way or another). If the device reads true, the poison is released and the cat dies. Otherwise the cat lives.</p>
          <p>According to quantum mechanics, whilst the cat is inside the box it is equal parts alive and dead. It is in a state of <em>superposition</em>, a blur of probability. Only when we open the box, and <em>observe</em> the cat, does the cat <em>collapse</em> into one or another of the possible definite states - alive, or dead.</p>
          <p>This thought experiment is known as “Schrödinger’s Cat”.</p>
        </div>
      </Section>
    </>
  );
};

const Schrodinger = props => (
  <StaticQuery
    query={graphql`
      query {
        schrodingerPhoto: file(relativePath: { eq: "assets/images/photos/schrodinger.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 500) {
              ...GatsbyImageSharpFluid_withWebp_tracedSVG
            }
          }
        }
      }
    `}
    render={data => <SchrodingerTemplate images={data} {...props} />}
  />
);

export default Schrodinger;
